import './wordpress.css'
import './article-style.scss'
import 'simplebar/dist/simplebar.min.css'

import parse, { attributesToProps, domToReact } from 'html-react-parser'
import _ from 'lodash'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import AddToCalendarHOC from 'react-add-to-calendar-hoc'
import { Helmet } from 'react-helmet'

import {
  AdsSection,
  Breadcrumb,
  ButtonModal,
  CalendarModal,
  ProgressBar,
  Shares,
  Title,
} from '@/components'
import EventDetail from '@/components/card-event-detail/card-event-detail'
import ArticleSeo from '@/components/seo/articleSeo'

import { ImageModal } from './modules/ImageModal'
import { RelatedListCard } from './modules/related-list-card'
import adjustDesktopTable from './utils/adjustDesktopTable'

const ArticleView = ({
  article,
  relatedPosts,
  category,
  subcategory,
  t,
  i18n,
  universityLevel,
  searchTag,
}) => {
  const title = _.get(article, 'title')

  const acf = _.get(article, 'acf')
  const excerpt =
    _.get(article, 'acf.articleExcerpt', '') !== null &&
    _.get(article, 'acf.articleExcerpt', '') !== ''
      ? _.get(article, 'acf.articleExcerpt')
      : _.get(article, 'excerpt')

  const tags = _.get(article, 'tags.nodes', [])

  let event
  let eventDatetime
  let eventLocation
  if (_.get(category, 'description') === 'events') {
    eventDatetime = _.get(acf, 'eventDatetime')
    eventLocation = _.get(acf, 'eventLocation')
    const startDate = _.get(eventDatetime, 'startDate')
    const startTime = _.get(eventDatetime, 'startTime')
    const endDate = _.get(eventDatetime, 'endDate')
    const endTime = _.get(eventDatetime, 'endTime')

    const st = moment(`${startDate} ${startTime} GMT`, 'DD/MM/YYYY HH:mm')
    const et = moment(`${endDate} ${endTime} GMT`, 'DD/MM/YYYY HH:mm')

    const duration = moment.duration(et.diff(st)).asHours()
    event = {
      description: _.get(article, 'excerpt'),
      duration,
      endDatetime: endDate
        ? et.format('YYYYMMDDTHHmmssZ')
        : st.format('YYYYMMDDTHHmmssZ'),
      location: eventLocation,
      startDatetime: st.format('YYYYMMDDTHHmmssZ'),
      title: _.get(article, 'title'),
    }
  }

  const AddToCalendarDropdown = AddToCalendarHOC(ButtonModal, CalendarModal)
  const isSSR = typeof window === 'undefined'
  const options = useMemo(() => {
    return {
      replace: (domNode) => {
        if (domNode.name === 'iframe') {
          return (
            <div className="iframe-video">
              <iframe
                className="responsive-iframe"
                src={domNode.attribs.src}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen={true}
              ></iframe>
            </div>
          )
        }

        if (
          domNode.name === 'video' &&
          domNode?.attribs?.src.endsWith('.webm')
        ) {
          const videoSrc = domNode?.attribs?.src
          return (
            <video loop playsInline autoPlay muted controls={false}>
              <source src={videoSrc} type={'video/webm'}></source>
            </video>
          )
        }

        // should not load widget js once page loaded
        if (
          domNode?.attribs?.src ===
          'https://crypto.com/price/static/widget/index.js'
        ) {
          return <></>
        }

        // hydrate cannot update dom attributes: https://reactjs.org/docs/react-dom.html#hydrate
        // https://www.gatsbyjs.com/docs/using-client-side-only-packages/#workaround-3-use-reactlazy-and-suspense-on-client-side-only
        // load widget js after dom load
        if (domNode?.attribs?.id?.startsWith('crypto-widget')) {
          if (isSSR) {
            return <></>
          } else {
            return (
              <React.Suspense fallback={<></>}>
                <Helmet>
                  <script src="https://crypto.com/price/static/widget/index.js" />
                </Helmet>
                <div {...attributesToProps(domNode.attribs)}></div>
              </React.Suspense>
            )
          }
        }

        if (domNode.name === 'table') {
          return (
            <div className="wp-table-wrap">
              {adjustDesktopTable(domNode, options)}
            </div>
          )
        }

        if (domNode.name === 'a') {
          //For pdf, to generate const pdf links
          const wordpressLinkRegex = new RegExp(
            `${process.env.CONTENT_HUB_ENDPOINT}/wp-content/uploads/`
          )
          if (
            domNode?.attribs?.href &&
            wordpressLinkRegex.test(domNode?.attribs?.href)
          ) {
            domNode.attribs.href = domNode.attribs.href.replace(
              `${process.env.CONTENT_HUB_ENDPOINT}/wp-content/uploads/`,
              `${process.env.ASSET_PREFIX}/wp-content/uploads/`
            )
          }
          return (
            <a {...attributesToProps(domNode.attribs)}>
              {domToReact(domNode.children, options)}
            </a>
          )
        }

        if (domNode?.attribs?.class?.startsWith('wp-block-button')) {
          if (domNode?.attribs?.class?.indexOf('is-style-outline') === -1) {
            domNode.attribs.class += ' is-style-fill'
            return (
              <button {...attributesToProps(domNode.attribs)}>
                {domToReact(domNode.children, options)}
              </button>
            )
          } else {
            return (
              <button {...attributesToProps(domNode.attribs)}>
                {domToReact(domNode.children, options)}
              </button>
            )
          }
        }
      },
    }
  }, [isSSR])

  const content = useMemo(
    () => parse((article?.content ?? '').replaceAll('\n', ''), options),
    [article, options]
  )

  const classNameCategory = (slug) => {
    let uniLevel
    if (slug) {
      switch (true) {
        case slug.startsWith('register'):
          uniLevel = 'register'
          break
        case slug.startsWith('intermediate'):
          uniLevel = 'intermediate'
          break
        case slug.startsWith('advanced'):
          uniLevel = 'advanced'
          break
        case slug.startsWith('beginner'):
          uniLevel = 'beginner'
          break
        default:
          break
      }
    }
    return uniLevel
  }

  const [selectedImage, setImage] = useState('')

  useEffect(() => {
    const articleDom = document.getElementById('article-content')
    const handleImage = (e) => {
      if (e.target.tagName === 'IMG') {
        setImage(e.target.src)
      }
    }
    articleDom?.addEventListener('click', handleImage)

    return () => {
      articleDom?.removeEventListener('click', handleImage)
    }
  }, [])

  return (
    <div>
      <ArticleSeo article={article} category={category} />
      <ImageModal selectedImage={selectedImage} setImage={setImage} />
      <ProgressBar />
      <Breadcrumb
        i18n={i18n}
        category={category}
        subcategory={subcategory}
        t={t}
      />
      <div className="article-container">
        <div className="article-box">
          <div className="article-box-title">
            <div>
              <h1 className="article-title">{title}</h1>
            </div>

            <div
              className="article-excerpt"
              dangerouslySetInnerHTML={{ __html: excerpt }}
            />

            {_.get(category, 'description') !== 'university' &&
              _.get(category, 'description') !== 'events' && (
                <div className="article-university-details">
                  <div>
                    <span className="article-university-details-date">
                      {_.get(article, 'date')}
                    </span>
                  </div>
                </div>
              )}
            {_.get(category, 'description') === 'university' && (
              <div className="article-university-details">
                <div>
                  <span className="article-university-details-date">
                    {_.get(article, 'date')}
                  </span>
                </div>
                <span>|</span>
                <div>
                  {universityLevel && (
                    <p
                      className={`article-university-details-level ${classNameCategory(
                        universityLevel.slug
                      )}`}
                    >
                      {universityLevel.name}
                    </p>
                  )}
                </div>
              </div>
            )}
            {_.get(category, 'description') === 'events' && (
              <div className="article-event-date">
                <EventDetail article={article} />
              </div>
            )}
          </div>
          <div className="article-box-content">
            <div id="article-content" className="article-box-text">
              {content}
            </div>
            {_.get(category, 'description') === 'events' && (
              <div className="add-to-calendar-box">
                <AddToCalendarDropdown
                  className={''}
                  linkProps={{
                    className: 'linkStyles',
                  }}
                  event={event}
                />
              </div>
            )}
            {!_.isEmpty(tags) && (
              <div className="article-box-tags">
                <h4 className="tag-title">Tags</h4>
                <div className="tag-box">
                  {_.map(tags, (tag) => (
                    <p
                      className="tag-text"
                      key={tag.name}
                      onClick={() => searchTag(tag.name)}
                      dangerouslySetInnerHTML={{ __html: tag.name }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="article-box-tags">
              <h4 className="tag-title">{t('article.share')}</h4>
              <Shares title={title} />
            </div>
          </div>
        </div>
        {!_.isEmpty(relatedPosts) && (
          <div className="article-related-content-box">
            <Title title="Related Content" />
            <div className="article-related-content-card-group">
              {_.map(relatedPosts, (card) => (
                <RelatedListCard
                  key={card.node.id}
                  item={_.get(card, 'node')}
                />
              ))}
            </div>
          </div>
        )}

        <AdsSection />
      </div>
    </div>
  )
}

export default ArticleView
